import { $, html, on, View } from 'rune-ts';
import { ButtonClose } from '../../../../shared/components/atoms/ButtonClose/ButtonClose';
import { typo } from '../../../../shared/typography/typo';
import { debounce } from '../../../../shared/util';
import { Icon } from '../Icon/Icon';
import klass from './Input.module.scss';

export class InputFocusoutEvent extends CustomEvent<never> {}
export class InputFocusinEvent extends CustomEvent<never> {}
export class InputChangedEvent extends CustomEvent<never> {}
export class InputResetAfterEvent extends CustomEvent<boolean> {}
export class InputResetEvent extends CustomEvent<boolean> {}

export type InputType = 'text' | 'password' | 'email' | 'tel' | 'url';
export interface InputProps {
  name: string;
  type?: InputType;
  value?: string;
  placeholder?: string;
  maxlength?: number;
  line?: number;
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  autofocus?: boolean;
  autocomplete?: boolean;
  delete_pattern?: RegExp;
  lang?: 'kr' | 'en' | 'jp';
  isError?: (value: string) => boolean;
}

export class InputControl extends View<InputProps> {
  resetButton: ButtonClose;

  constructor(props: InputProps) {
    super(props);
    this.resetButton = new ButtonClose({ hide: true, tag: 'span' });
  }

  protected override template() {
    return html` <div class="${klass.input_controller}">
      ${this.data.maxlength
        ? html`<div class="${klass.maxlength} ${typo('12_medium')}">
            <span class="value_length">${this.data.value?.length ?? 0}</span> /
            ${ET('mps2::common::word_length', {
              length: this.data.maxlength,
            })}
          </div>`
        : ''}
      ${this.data.line ? '' : this.resetButton}
    </div>`;
  }

  protected override onRender() {
    this.delegate('click', ButtonClose, () => {
      this.dispatchEvent(InputResetEvent, { bubbles: true });
    });
  }

  override redraw(): this {
    const { value, line } = this.data;

    $(this.element())
      .find('.value_length')
      ?.setTextContent(value ? value.length.toString() : '0');

    if (line) return this;

    if (value && this.resetButton.data.hide) {
      this.resetButton.show();
    }

    if (!value) {
      this.resetButton.immediateHide();
    }

    return this;
  }

  focusout() {
    if (this.data.line) return;
    this.resetButton.hide();
  }
}

export class InputView extends View<InputProps> {
  type: InputType = 'text';
  value: number | string;

  constructor(data: InputProps) {
    super(data);
    this.type = data.type ?? this.type;
    this.value = data.value ?? '';
  }

  protected override template() {
    const { line, placeholder, name, maxlength, readonly, required, autofocus, disabled, autocomplete } =
      this.data;

    if (line) {
      return html`
        <textarea
          class="${klass.input} ${typo('14_medium')}"
          type="${this.type}"
          placeholder="${placeholder ?? ''}"
          value="${this.value}"
          name="${name}"
          rows="${line}"
          ${maxlength && maxlength > 0 ? `maxlength=${maxlength}` : ''}
          ${required ? 'required' : ''}
          ${autofocus ? 'autofocus' : ''}
          ${readonly || disabled ? 'disabled' : ''}
          autocomplete="${autocomplete ? 'on' : 'off'}"
          data-readonly="${!!readonly}"
        >
${this.value}</textarea
        >
      `;
    }

    return html` <input
      class="${klass.input} ${typo('14_medium')}"
      type="${this.type}"
      placeholder="${placeholder ?? ''}"
      value="${this.value}"
      name="${name}"
      ${maxlength && maxlength > 0 ? `maxlength=${maxlength}` : ''}
      ${required ? 'required' : ''}
      ${autofocus ? 'autofocus' : ''}
      ${readonly || disabled ? 'disabled' : ''}
      ${autocomplete ? 'autocomplete' : ''}
      data-readonly="${!!readonly}"
    />`;
  }

  override redraw(): this {
    const input = this.element() as HTMLInputElement;
    const { placeholder, readonly, disabled, required, autofocus } = this.data;

    input.placeholder = placeholder ?? '';
    input.type = this.type;
    input.value = this.value.toString();
    input.disabled = !!(readonly || disabled);
    input.required = !!required;
    input.autofocus = !!autofocus;
    input.dataset.readonly = (!!readonly).toString();

    return this;
  }

  reset() {
    const input = this.element() as HTMLInputElement;
    input.value = '';
    this.value = input.value;
  }

  setValue(value: number | string) {
    const input = this.element() as HTMLInputElement;
    input.value = value.toString();
    this.value = input.value;
  }

  getValue() {
    const input = this.element() as HTMLInputElement;
    this.value = input.value;
    return input.value;
  }
}

export class Input extends View<InputProps> {
  inputView: InputView;
  inputControlView: InputControl;
  constructor(data: InputProps) {
    data.value = data.value?.replace(/&amp;/g, '&');
    super(data);
    this.inputView = new InputView(data);
    this.inputControlView = new InputControl(data);
  }

  override template() {
    const { line, readonly, disabled, lang } = this.data;

    return html`<label
      data-error="false"
      data-line="${!!line}"
      ${readonly || disabled ? 'disabled' : ''}
      class="${klass.input_container}"
    >
      ${lang ? new Icon({ name: `${lang}_flag`, size: 18 }) : ''} ${this.inputView} ${this.inputControlView}
    </label> `;
  }

  override redraw(): this {
    const { readonly, disabled } = this.data;
    if (readonly || disabled) this.element().setAttribute('disabled', '');
    else this.element().removeAttribute('disabled');
    this.subViewsFromTemplate.forEach((view) => view.redraw());
    return this;
  }

  protected override onRender() {
    this.addEventListener('focusin', () => {
      this.dispatchEvent(InputFocusinEvent, {});
    });
    this.addEventListener('focusout', () => {
      this.dispatchEvent(InputFocusoutEvent, {});
    });
    this.delegate('change', InputView, (e, input) => {
      this.changeHandler(e, input);
      this.dispatchEvent(InputChangedEvent, {});
    });
    this.delegate('keyup', InputView, (e, input) => {
      this.changeHandler(e, input);
      this.dispatchEvent(InputChangedEvent, {});
    });
  }

  changeHandler(_e: Event, input: InputView) {
    if (this.data.delete_pattern) {
      const origin_value = input.getValue();
      const result_value = origin_value.replace(this.data.delete_pattern, '');
      input.setValue(result_value);
      this.data.value = result_value;
    } else {
      this.data.value = input.getValue();
    }
  }

  @on(InputChangedEvent)
  changedHandler() {
    if (!this.data.isError) return;
    return this.errorHandler();
  }

  errorHandler = debounce(() => {
    if (!this.data.isError) return;
    const is_error = !this.data.value ? false : this.data.isError(this.data.value);
    this.element().dataset.error = String(is_error);
  }, 50);

  @on(InputResetAfterEvent)
  resetAfterHandler() {
    this.element().dataset.error = String(false);
  }

  @on(InputResetEvent)
  resetHandler() {
    this.data.value = '';
    this.inputView.reset();
    this.inputControlView.redraw();
    this.dispatchEvent(InputResetAfterEvent, {});
  }

  @on(InputFocusinEvent)
  @on(InputChangedEvent)
  redrawHandler() {
    this.inputControlView.redraw();
  }

  @on(InputFocusoutEvent)
  focusoutHandler() {
    this.inputControlView.focusout();
  }

  blur() {
    this.inputView.element().blur();
  }

  focus() {
    this.inputView.element().focus();
  }

  getSelector() {
    return '.' + this.toString() + '[name="' + this.data.name + '"]';
  }

  getValue() {
    return this.data.value ?? '';
  }

  setValue(text: string) {
    this.data.value = text;
    this.inputView.setValue(text);
  }
}
